"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingDown, RefreshCw, UserCheck, SearchCode, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const hiringScenarios = [{
  icon: TrendingDown,
  title: "Organic traffic has stalled or declined",
  description: "When core rankings drop after a Google core update or competitors overtake key commercial terms in your market, I diagnose the exact algorithmic or technical cause and build a recovery plan.",
  linkText: "Request an SEO audit",
  linkTo: "/SeoAuditsPage"
}, {
  icon: RefreshCw,
  title: "Planning a major site migration or rebuild",
  description: "Relaunching a website without SEO oversight often wipes out historical rankings overnight. I map URLs, manage redirects, and ensure technical parity before launch across all regional versions.",
  linkText: "Explore technical SEO support",
  linkTo: "/TechnicalSeoPage"
}, {
  icon: UserCheck,
  title: "Deciding whether to hire an agency or an independent consultant",
  description: "Engage an independent consultant when your business needs direct senior oversight, rapid technical execution, and dedicated accountability for high-priority organic search initiatives.",
  linkText: "Read about the agency alternative",
  linkTo: "/SEOAgencyAlternativeUkPage"
}, {
  icon: SearchCode,
  title: "Expanding into competitive B2B or ecommerce spaces",
  description: "Whether you need high-intent qualified leads or deeper transactional visibility across domestic and global markets, I tailor the organic search architecture to revenue goals.",
  linkText: "View B2B lead generation",
  linkTo: "/B2BSeoLeadGenerationPage"
}];
const directBenefits = ["One-to-one communication with me across every project stage.", "Tailored search strategies built strictly around your commercial margins and target markets", "Proactive technical diagnosis and developer-ready implementation specs", "Preparation for modern search, including Google AI Overviews and regional SERP features"];
export default function WhenToHire() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="whentohireconsultant" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
        
        {/* Header and Featured Snippet Block */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider text-primary">
            Clear Guidance
          </Badge>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground tracking-tight mb-6">
            When should your business hire an SEO consultant?
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Whether operating in a single territory or competing across worldwide markets, hiring an independent SEO consultant is the right choice when you need senior technical direction and commercial clarity without agency bloat. Look for an advisor with proven technical depth, hands-on implementation skills, and direct accountability.
          </p>
        </div>

        {/* Snippet Card (Optimised for Quick Reference) */}
        <div className="bg-card border border-border rounded-xl p-6 md:p-8 shadow-xs mb-14 md:mb-18">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0">
              <Sparkles className="size-5" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                Quick answer: The ideal point to engage an expert
              </h3>
              <p className="text-muted-foreground leading-relaxed text-base">
                Businesses worldwide hire an independent SEO consultant when internal marketing teams lack dedicated technical search depth, when rankings plateau despite ongoing content production, or when scaling into competitive global territories. A dedicated consultant works closely within your workflow to resolve complex indexing, architecture, and revenue bottlenecks.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-4 border-t border-border mt-6">
            {directBenefits.map((benefit, i) => <div key={i} data-index={i} className="flex items-center gap-2.5">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span className="text-sm font-medium text-foreground">{benefit}</span>
              </div>)}
          </div>
        </div>

        {/* 4 Core Situations Grid */}
        <div className="mb-14">
          <div className="text-left mb-8">
            <h3 className="text-2xl font-serif font-bold text-foreground">
              Four common signs your business is ready for SEO consulting
            </h3>
            <p className="text-muted-foreground text-sm mt-1">
              Every client situation is unique, but most initial consultations originate from one of these pivotal moments.
            </p>
          </div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-6" variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.15
        }}>
            {hiringScenarios.map((scenario, i) => {
            const IconComponent = scenario.icon;
            return <motion.div key={scenario.title} variants={itemVariants} data-index={i} className="h-full">
                  <Card className="h-full bg-card border-border hover:border-primary/40 shadow-xs transition-[border-color,box-shadow] flex flex-col justify-between p-6 rounded-xl">
                    <CardHeader className="p-0 mb-4">
                      <div className="size-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                        <IconComponent className="size-5" />
                      </div>
                      <CardTitle className="text-lg font-semibold text-foreground">
                        {scenario.title}
                      </CardTitle>
                      <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                        {scenario.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="p-0 pt-3">
                      <Link to={scenario.linkTo} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors">
                        <span>{scenario.linkText}</span>
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Bottom Conversion Action Bar */}
        <div className="rounded-2xl bg-muted/60 border border-border p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="max-w-xl">
            <h3 className="text-xl md:text-2xl font-serif font-bold text-foreground mb-2">
              Unsure if you need an audit or ongoing consulting?
            </h3>
            <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
              Book a straightforward conversation with me. I will review your current search visibility, discuss your immediate objectives, and help decide what makes commercial sense.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/SeoAuditsPage">
                Get a Free SEO Audit
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}